"use client"
import { useRef, useEffect, useState } from "react"
import {
  Trash2,
  Search,
  Folder,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Filter,
  X,
  Loader,
  AlertCircle,
  FolderOpen,
  FileText,
  ArrowUpRight,
  Info,
  XCircle,
  ArrowLeft,
  Calendar,
  Plus,
  ArrowUpDown,
  MessageCircle,
  GitBranch,
  Network,
  Bot,
  PenTool,
  Save,
  FileUp,
  Grid3X3,
  List,
  MoreVertical,
  Edit,
} from "lucide-react"
import Flashcard from "./FlashCard"
import ReactMarkdown from "react-markdown"
import { BlockMath, InlineMath } from "react-katex"
import "katex/dist/katex.min.css"
import FlowChart from "./FlowChart"
import MindMap from "./MindMap"
import { Excalidraw } from "@excalidraw/excalidraw"
import { getAllnotes } from "./api"
import MnemonicDevice from "./MnemonicDevice"
import { postAws } from "../aws"
import { generatePresignedUrl, postTeacherExcallyNotes } from "../postFileAws"
import { v4 as uuidv4 } from "uuid"
import UploadPdfForDigitisation from "./UploadPdfForDigitisation"

const Notes = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [notes, setNotes] = useState([])
  const [notesFetched, setNotesFetched] = useState(false)
  const [organizedNotes, setOrganizedNotes] = useState({})
  const [expandedFolder, setExpandedFolder] = useState(null)
  const [copyObj, setCopyObj] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeFilter, setActiveFilter] = useState("all")
  const [sortOrder, setSortOrder] = useState("newest")
  const [favoriteNotes, setFavoriteNotes] = useState([])
  const [fullScreenNote, setFullScreenNote] = useState(null)
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false)
  const [newNoteData, setNewNoteData] = useState({ folderName: "", topic: "" })
  const [showOptionsForPage, setShowOptionsForPage] = useState(null)
  const [fullscreenContent, setFullscreenContent] = useState(null)
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [savingStates, setSavingStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [excalidrawFullscreen, setExcalidrawFullscreen] = useState(null)
  const [isPdfUploadModalOpen, setIsPdfUploadModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState("notes")
  const [digitisedNotes, setDigitisedNotes] = useState([])
  const [isLoadingDigitised, setIsLoadingDigitised] = useState(false)
  const excalidrawRefs = useRef({})
  const [viewMode, setViewMode] = useState("card") // New state for view toggle
  const [activeNoteMenu, setActiveNoteMenu] = useState(null) // For list view dropdown

  // Removed unused states: `showFilters`, `showMobileSearch`, `searchInputRef`, `isAddFolderNoteModalOpen`, `selectedFolder`

  const saveExcalidrawNote = async (noteId, pageIndex, side, refId, noteTitle) => {
    const excalidrawAPI = excalidrawRefs.current[refId]
    if (!excalidrawAPI) {
      console.error("Excalidraw API not found for refId:", refId)
      return
    }
    try {
      setSavingStates((prev) => ({ ...prev, [refId]: true }))
      const elements = excalidrawAPI.getSceneElements()
      const appState = excalidrawAPI.getAppState()
      const excalidrawData = {
        type: "excalidraw",
        version: 2,
        source: "https://excalidraw.com",
        elements: elements,
        appState: {
          gridSize: appState.gridSize,
          viewBackgroundColor: appState.viewBackgroundColor,
          currentItemStrokeColor: appState.currentItemStrokeColor,
          currentItemBackgroundColor: appState.currentItemBackgroundColor,
          currentItemFillStyle: appState.currentItemFillStyle,
          currentItemStrokeWidth: appState.currentItemStrokeStyle,
          currentItemRoughness: appState.currentItemRoughness,
          currentItemOpacity: appState.currentItemOpacity,
          currentItemFontFamily: appState.currentItemFontFamily,
          currentItemFontSize: appState.currentItemFontSize,
          currentItemTextAlign: appState.currentItemTextAlign,
          currentItemStartArrowhead: appState.currentItemStartArrowhead,
          currentItemEndArrowhead: appState.currentItemEndArrowhead,
          scrollX: appState.scrollX,
          scrollY: appState.scrollY,
          zoom: appState.zoom,
          currentItemRoundness: appState.currentItemRoundness,
          gridColor: appState.gridColor,
          currentStrokeOptions: appState.currentStrokeOptions,
          previousGridSize: appState.previousGridSize,
          frameRendering: appState.frameRendering,
        },
        files: {},
      }
      const jsonString = JSON.stringify(excalidrawData)
      const jsonBlob = new Blob([jsonString], { type: "application/json" })
      const fileName = `${noteTitle || refId}.json`
      const file = new File([jsonBlob], fileName, { type: "application/json" })
      const teacherId = localStorage.getItem("teacherId")
      const uploadResult = await postTeacherExcallyNotes(file, teacherId, fileName, refId)
      if (uploadResult && uploadResult.uri) {
        const saveData = {
          request: "save-excalidraw-note",
          body: {
            noteId,
            pageIndex,
            side,
            s3Uri: uploadResult.uri,
          },
        }
        const backendResponse = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", saveData)
        if (backendResponse.statusCode === 200) {
          setFullScreenNote((prevNote) => {
            if (!prevNote || prevNote._id !== noteId) return prevNote
            const updatedNote = { ...prevNote }
            const page = { ...updatedNote.noteContents[pageIndex] }
            page[side] = {
              noteType: "manual",
              noteData: uploadResult.uri,
            }
            updatedNote.noteContents[pageIndex] = page
            return updatedNote
          })
          setNotes((prevNotes) =>
            prevNotes.map((note) => {
              if (note._id === noteId) {
                const updatedNote = { ...note }
                const page = { ...updatedNote.noteContents[pageIndex] }
                page[side] = {
                  noteType: "manual",
                  noteData: uploadResult.uri,
                }
                updatedNote.noteContents[pageIndex] = updatedNote
                return updatedNote
              }
              return note
            }),
          )
          setFullscreenContent((prevContent) => {
            if (
              prevContent &&
              prevContent._noteId === noteId &&
              prevContent._pageIndex === pageIndex &&
              prevContent._side === side
            ) {
              return {
                ...prevContent,
                noteData: uploadResult.uri,
              }
            }
            return prevContent
          })
          const successMessage = document.createElement("div")
          successMessage.className = "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
          successMessage.textContent = "Drawing saved successfully!"
          document.body.appendChild(successMessage)
          setTimeout(() => {
            if (document.body.contains(successMessage)) {
              document.body.removeChild(successMessage)
            }
          }, 3000)
        }
      }
    } catch (error) {
      console.error("Error saving Excalidraw note:", error)
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = "Failed to save drawing. Please try again."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 3000)
    } finally {
      setSavingStates((prev) => ({ ...prev, [refId]: false }))
    }
  }

  const loadExcalidrawNote = async (s3Uri, refId) => {
    if (!s3Uri) return
    try {
      setLoadingStates((prev) => ({ ...prev, [refId]: true }))
      console.log("Loading Excalidraw note:", s3Uri, "for refId:", refId)
      let correctedS3Uri = s3Uri
      if (s3Uri.includes("eachers/")) {
        correctedS3Uri = s3Uri.replace("eachers/", "Teachers/")
      }
      const presignedUrl = await generatePresignedUrl(correctedS3Uri)
      console.log("Generated presigned URL:", presignedUrl)
      if (presignedUrl) {
        const response = await fetch(presignedUrl)
        if (response.ok) {
          const excalidrawData = await response.json()
          console.log("Loaded Excalidraw data:", excalidrawData)
          const excalidrawAPI = excalidrawRefs.current[refId]
          console.log("Found excalidraw API:", !!excalidrawAPI, "for refId:", refId)
          if (excalidrawAPI) {
            setTimeout(() => {
              try {
                excalidrawAPI.updateScene({
                  elements: excalidrawData.elements || [],
                  appState: {
                    ...excalidrawData.appState,
                    zoom: excalidrawData.appState?.zoom || { value: 1 },
                    scrollX: excalidrawData.appState?.scrollX || 0,
                    scrollY: excalidrawData.appState?.scrollY || 0,
                  },
                  files: excalidrawData.files || {},
                })
                console.log("Successfully updated scene with elements:", excalidrawData.elements?.length || 0)
              } catch (updateError) {
                console.error("Error updating Excalidraw scene:", updateError)
              }
            }, 100)
          } else {
            console.warn("Excalidraw API not found for refId:", refId)
          }
        } else {
          console.error("Failed to fetch Excalidraw data:", response.status, response.statusText)
        }
      }
    } catch (error) {
      console.error("Error loading Excalidraw note:", error)
    } finally {
      setTimeout(() => {
        setLoadingStates((prev) => ({ ...prev, [refId]: false }))
      }, 1000)
    }
  }

  const renderLatex = (text) => {
    if (!text) return null
    const blockLatexRegex = /\[\s*([\s\S]*?)\s*\]/g
    const inlineLatexRegex = /\$\$\s*([\s\S]*?)\s*\$\$/g
    const parts = text.split(blockLatexRegex)
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return <BlockMath key={`block-${index}`}>{part}</BlockMath>
      } else {
        const inlineParts = part.split(inlineLatexRegex)
        return inlineParts.map((inlinePart, inlineIndex) => {
          if (inlineIndex % 2 === 1) {
            return <InlineMath key={`inline-${index}-${inlineIndex}`}>{inlinePart}</InlineMath>
          } else {
            return (
              <ReactMarkdown
                key={`text-${index}-${inlineIndex}`}
                components={{
                  p: ({ children }) => <p className="mb-4">{children}</p>,
                  h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-bold mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-bold mb-2">{children}</h3>,
                  ul: ({ children }) => <ul className="list-disc pl-6 mb-4">{children}</ul>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                }}
              >
                {inlinePart}
              </ReactMarkdown>
            )
          }
        })
      }
    })
  }

  useEffect(() => {
    const getNotes = async () => {
      setIsLoading(true)
      try {
        const allNotes = await getAllnotes(localStorage.getItem("classRoomId"), localStorage.getItem("teacherId"))
        const notesArray = allNotes.body.data
        setNotes([...notesArray])
        organizeNotes([...notesArray])
        const savedFavorites = localStorage.getItem("favoriteNotes")
        if (savedFavorites) {
          setFavoriteNotes(JSON.parse(savedFavorites))
        }
      } catch (err) {
        console.error("Error fetching notes:", err)
        setError("Failed to load notes. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }
    if (!notesFetched) {
      getNotes()
      setNotesFetched(true)
    }
  }, [notesFetched])

  useEffect(() => {
    localStorage.setItem("favoriteNotes", JSON.stringify(favoriteNotes))
  }, [favoriteNotes])

  useEffect(() => {
    if (fullScreenNote) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [fullScreenNote])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptionsForPage !== null && !event.target.closest(".options-menu")) {
        setShowOptionsForPage(null)
      }
      if (activeNoteMenu !== null && !event.target.closest(".options-menu-list")) {
        setActiveNoteMenu(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showOptionsForPage, activeNoteMenu])

  useEffect(() => {
    window.switchToDigitisedNotes = () => {
      setActiveTab("digitised")
      fetchDigitisedNotes()
    }
    return () => {
      delete window.switchToDigitisedNotes
    }
  }, [])

  const fetchDigitisedNotes = async () => {
    setIsLoadingDigitised(true)
    try {
      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classroomId) {
        throw new Error("Missing required user data")
      }
      const payload = {
        request: "get-digital-notes",
        body: {
          teacherId,
          classroomId,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/DigitiseNotesFromPdf/getNotes", "POST", payload)
      if (response.statusCode === 200) {
        const responseBody = typeof response.body === "string" ? JSON.parse(response.body) : response.body
        setDigitisedNotes(responseBody.notes || [])
      } else {
        throw new Error("Failed to fetch digitised notes")
      }
    } catch (error) {
      console.error("Error fetching digitised notes:", error)
      setError("Failed to load digitised notes. Please try again.")
    } finally {
      setIsLoadingDigitised(false)
    }
  }

  useEffect(() => {
    if (activeTab === "digitised") {
      fetchDigitisedNotes()
    }
  }, [activeTab])

  const copyToClipboard = async (textToCopy, noteId) => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      setCopyObj({
        ...copyObj,
        [noteId]: true,
      })
      setTimeout(() => {
        setCopyObj((prev) => ({
          ...prev,
          [noteId]: false,
        }))
      }, 2000)
    } catch (err) {
      console.error("Failed to copy text: ", err)
    }
  }

  const organizeNotes = (notesData) => {
    const organized = notesData.reduce((acc, note) => {
      const noteFolder = note.noteFolder || "Uncategorized"
      if (!acc[noteFolder]) {
        acc[noteFolder] = []
      }
      acc[noteFolder].push(note)
      return acc
    }, {})
    setOrganizedNotes(organized)
  }

  const toggleFolder = (noteFolder) => {
    setExpandedFolder(expandedFolder === noteFolder ? null : noteFolder)
  }

  const toggleFavorite = (noteId) => {
    setFavoriteNotes((prev) => {
      if (prev.includes(noteId)) {
        return prev.filter((id) => id !== noteId)
      } else {
        return [...prev, noteId]
      }
    })
  }

  const getFilteredNotes = () => {
    const searchFiltered = Object.entries(organizedNotes).reduce((acc, [noteFolder, folderNotes]) => {
      const filtered = folderNotes.filter(
        (note) =>
          note?.noteTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          noteFolder.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      if (filtered.length > 0) {
        acc[noteFolder] = filtered
      }
      return acc
    }, {})

    if (activeFilter === "all") {
      return searchFiltered
    }

    if (activeFilter === "favorites") {
      return Object.entries(searchFiltered).reduce((acc, [noteFolder, folderNotes]) => {
        const filtered = folderNotes.filter((note) => favoriteNotes.includes(note._id))
        if (filtered.length > 0) {
          acc[noteFolder] = filtered
        }
        return acc
      }, {})
    }

    return Object.entries(searchFiltered).reduce((acc, [noteFolder, folderNotes]) => {
      const filtered = folderNotes.filter((note) => {
        if (!note || !note.noteContents || !Array.isArray(note.noteContents)) {
          return false
        }
        return note.noteContents.some((page) => {
          if (!page || !page.aSide || !page.bSide) {
            return false
          }
          return (
            (page.aSide.noteType && page.aSide.noteType.toLowerCase() === activeFilter.toLowerCase()) ||
            (page.bSide.noteType &&
              page.bSide.noteType !== "NONE" &&
              page.bSide.noteType.toLowerCase() === activeFilter.toLowerCase())
          )
        })
      })
      if (filtered.length > 0) {
        acc[noteFolder] = filtered
      }
      return acc
    }, {})
  }

  const getFlattenedFilteredNotes = () => {
    const filteredByFolders = getFilteredNotes()
    let allNotes = []
    Object.values(filteredByFolders).forEach((folderNotes) => {
      allNotes = allNotes.concat(folderNotes)
    })
    return sortNotes(allNotes)
  }

  const sortNotes = (notes) => {
    return [...notes].sort((a, b) => {
      if (sortOrder === "newest") {
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      } else if (sortOrder === "oldest") {
        return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
      } else if (sortOrder === "alphabetical") {
        return (a.noteTitle || "").localeCompare(b.noteTitle || "")
      }
      return 0
    })
  }

  const getNoteTypeIcon = (type) => {
    if (!type) return <FileText className="text-gray-500" size={16} />
    const lowerType = type.toLowerCase()
    switch (lowerType) {
      case "flashcard":
        return <BookOpen className="text-orange-500" size={16} />
      case "manual":
        return <PenTool className="text-purple-500" size={16} />
      case "text": // Changed from 'chat' to 'text'
        return <MessageCircle className="text-blue-500" size={16} />
      case "flowchart":
        return <GitBranch className="text-green-500" size={16} />
      case "mindmap":
        return <Network className="text-pink-500" size={16} />
      case "mnemonic":
        return <Bot className="text-indigo-500" size={16} />
      default:
        return <FileText className="text-gray-500" size={16} />
    }
  }

  const fetchFreshNoteData = async (noteId) => {
    try {
      const response = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", {
        request: "get-note-by-id",
        body: {
          noteId: noteId,
        },
      })
      if (response && response.body && response.body.data) {
        const freshNote = response.body.data
        setNotes((prevNotes) => prevNotes.map((note) => (note._id === noteId ? freshNote : note)))
        setOrganizedNotes((prevOrganized) => {
          const updated = { ...prevOrganized }
          Object.keys(updated).forEach((folder) => {
            updated[folder] = updated[folder].map((note) => (note._id === noteId ? freshNote : note))
          })
          return updated
        })

        setFullscreenContent((prevContent) => {
          if (prevContent && prevContent._noteId === noteId) {
            const freshContent = freshNote.noteContents[prevContent._pageIndex][prevContent._side]
            return {
              ...freshContent,
              _noteId: noteId,
              _pageIndex: prevContent._pageIndex,
              _side: prevContent._side,
            }
          }
          return prevContent
        })
        return freshNote
      }
    } catch (error) {
      console.error("Error fetching fresh note data:", error)
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = "Failed to sync changes. Please refresh the page."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 5000)
    }
    return null
  }

  const openFullScreenNote = async (note) => {
    // Immediately set the note to display it
    setFullScreenNote(note)
    setShowOptionsForPage(null) // Close any open options menus

    // Then, fetch fresh data in the background to update the displayed note
    const freshNote = await fetchFreshNoteData(note._id)
    if (freshNote) {
      // If fresh data is available, update the fullScreenNote state again
      setFullScreenNote(freshNote)
    }
  }

  const closeFullScreenNote = async () => {
    if (fullScreenNote) {
      try {
        setIsLoading(true)
        const freshNote = await fetchFreshNoteData(fullScreenNote._id)
        if (freshNote) {
          setNotes((prevNotes) => prevNotes.map((note) => (note._id === freshNote._id ? freshNote : note)))
          const allNotes = await getAllnotes(localStorage.getItem("classRoomId"), localStorage.getItem("teacherId"))
          const notesArray = allNotes.body.data
          setNotes([...notesArray])
          organizeNotes([...notesArray])
        }
      } catch (error) {
        console.error("Error syncing data:", error)
      } finally {
        setIsLoading(false)
      }
    }
    setFullScreenNote(null)
    setShowOptionsForPage(null)
    setExpandedFolder(null)
    setSearchTerm("")
    setActiveFilter("all")
    document.body.style.overflow = "auto"
  }

  const openExcalidrawFullscreen = (content, noteId, pageIndex, side) => {
    setExcalidrawFullscreen({
      ...content,
      _noteId: noteId,
      _pageIndex: pageIndex,
      _side: side,
    })
  }

  const closeExcalidrawFullscreen = async () => {
    if (excalidrawFullscreen) {
      await fetchFreshNoteData(excalidrawFullscreen._noteId)
    }
    setExcalidrawFullscreen(null)
  }

  const openContentFullscreen = (content, noteId, pageIndex, side) => {
    setFullscreenContent({
      ...content,
      _noteId: noteId,
      _pageIndex: pageIndex,
      _side: side,
    })
  }

  const handleDeleteNote = async (noteId) => {
    setIsDeleting(true)
    try {
      const deleteData = {
        request: "delete-Note",
        noteId: noteId,
      }
      const response = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", deleteData)
      setNotes(notes.filter((note) => note._id !== noteId))
      const updatedOrganizedNotes = {}
      Object.entries(organizedNotes).forEach(([folder, folderNotes]) => {
        const filteredNotes = folderNotes.filter((note) => note._id !== noteId)
        if (filteredNotes.length > 0) {
          updatedOrganizedNotes[folder] = filteredNotes
        }
      })
      setOrganizedNotes(updatedOrganizedNotes)
      if (favoriteNotes.includes(noteId)) {
        setFavoriteNotes(favoriteNotes.filter((id) => id !== noteId))
      }
      if (fullScreenNote && fullScreenNote._id === noteId) {
        setFullScreenNote(null)
      }
      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
    } catch (err) {
      console.error("Error deleting note:", err)
      setError("Failed to delete note. Please try again.")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteFolder = async (folderName) => {
    setIsDeleting(true)
    try {
      const deleteData = {
        request: "delete-NoteFolder",
        noteFolder: folderName,
        userId: localStorage.getItem("teacherId"),
      }
      const response = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", deleteData)
      const notesInFolder = organizedNotes[folderName] || []
      const noteIdsInFolder = notesInFolder.map((note) => note._id)
      setNotes(notes.filter((note) => !noteIdsInFolder.includes(note._id)))
      const { [folderName]: _, ...restFolders } = organizedNotes
      setOrganizedNotes(restFolders)
      if (noteIdsInFolder.some((id) => favoriteNotes.includes(id))) {
        setFavoriteNotes(favoriteNotes.filter((id) => !noteIdsInFolder.includes(id)))
      }
      if (fullScreenNote && fullScreenNote.noteFolder === folderName) {
        setFullScreenNote(null)
      }
      if (expandedFolder === folderName) {
        setExpandedFolder(null)
      }
      setIsDeleteModalOpen(false)
      setDeleteTarget(null)
    } catch (err) {
      console.error("Error deleting folder:", err)
      setError("Failed to delete folder. Please try again.")
    } finally {
      setIsDeleting(false)
    }
  }

  const openDeleteModal = (target) => {
    setDeleteTarget(target)
    setIsDeleteModalOpen(true)
  }

  const renderNotePreview = (content, noteId, pageIndex, side) => {
    if (!content) return null
    const { noteType, noteData } = content
    if (!noteType) return null
    const lowerType = noteType.toLowerCase()
    const handleClick = () => {
      if (lowerType === "manual") {
        openExcalidrawFullscreen(content, noteId, pageIndex, side)
      } else if (lowerType === "flashcard") {
        openContentFullscreen(content, noteId, pageIndex, side)
      } else if (lowerType === "mnemonic") {
        openContentFullscreen(content, noteId, pageIndex, side)
      } else if (lowerType === "mindmap") {
        openContentFullscreen(content, noteId, pageIndex, side)
      } else if (lowerType === "flowchart") {
        openContentFullscreen(content, noteId, pageIndex, side)
      } else if (lowerType === "text") {
        // Changed from 'chat' to 'text'
        openContentFullscreen(content, noteId, pageIndex, side)
      }
    }
    return (
      <div className="w-full h-full overflow-hidden relative">
        {lowerType === "flashcard" ? (
          <div className="bg-white rounded-lg w-full h-full cursor-pointer" onClick={handleClick}>
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-orange-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-all">
              <div className="text-center">
                <BookOpen size={48} className="text-orange-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Click to open flashcard</p>
                <p className="text-gray-400 text-sm">Interactive learning</p>
              </div>
            </div>
          </div>
        ) : lowerType === "manual" ? (
          <div className="bg-white rounded-lg w-full h-full cursor-pointer" onClick={handleClick}>
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all">
              <div className="text-center">
                <PenTool size={48} className="text-purple-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Click to open drawing</p>
                <p className="text-gray-400 text-sm">Opens in fullscreen</p>
              </div>
            </div>
          </div>
        ) : lowerType === "mnemonic" ? (
          <div className="bg-white rounded-lg w-full h-full cursor-pointer" onClick={handleClick}>
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-indigo-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all">
              <div className="text-center">
                <Bot size={48} className="text-indigo-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Click to open mnemonic</p>
                <p className="text-gray-400 text-sm">Memory aid device</p>
              </div>
            </div>
          </div>
        ) : lowerType === "mindmap" ? (
          <div className="bg-white rounded-lg w-full h-full cursor-pointer" onClick={handleClick}>
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-pink-300 rounded-lg hover:border-pink-400 hover:bg-pink-50 transition-all">
              <div className="text-center">
                <Network size={48} className="text-pink-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Click to open mind map</p>
                <p className="text-gray-400 text-sm">Visual thinking tool</p>
              </div>
            </div>
          </div>
        ) : lowerType === "flowchart" ? (
          <div className="bg-white rounded-lg w-full h-full cursor-pointer" onClick={handleClick}>
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-green-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all">
              <div className="text-center">
                <GitBranch size={48} className="text-green-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Click to open flowchart</p>
                <p className="text-gray-400 text-sm">Process diagram</p>
              </div>
            </div>
          </div>
        ) : lowerType === "text" ? ( // Changed from 'chat' to 'text'
          <div className="bg-white rounded-lg w-full h-full cursor-pointer" onClick={handleClick}>
            <div className="w-full h-full flex items-center justify-center border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all">
              <div className="text-center">
                <MessageCircle size={48} className="text-blue-400 mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Click to open text</p>
                <p className="text-gray-400 text-sm">Rich content</p>
              </div>
            </div>
          </div>
        ) : lowerType === "none" ? (
          <div className="flex items-center justify-center h-full">
            <button className="p-4 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
              <Plus size={24} className="text-gray-500" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <Info size={20} className="mr-2" />
            <span>Unsupported note type: {noteType}</span>
          </div>
        )}
      </div>
    )
  }

  const handleAddNewContentType = async (pageIndex, side, contentType, userTopic) => {
    if (!fullScreenNote) return
    if (contentType === "manual") {
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }
      const refId = uuidv4()
      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: {
            noteType: contentType,
            noteData: contentType === "manual" ? refId : "new note",
          },
        },
        request: "add-Info-page",
      }
      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
      page[side] = {
        noteType: contentType,
        noteData: contentType === "manual" ? refId : "new note",
      }
      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }
    if (contentType === "flashcard") {
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }
      // Send user topic for AI generation
      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: userTopic || "Create a flashcard", // Send user's topic
          noteType: contentType,
        },
        request: "add-Info-page",
      }
      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
      const aiNoteData = JSON.parse(updateData.aiData)
      page[side] = {
        noteType: contentType,
        noteData: aiNoteData,
      }
      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }
    if (contentType === "mnemonic") {
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }
      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: userTopic || "Create a mnemonic device", // Send user's topic
          noteType: "mnemonic",
        },
        request: "add-Info-page",
      }
      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
      const aiNoteData = JSON.parse(updateData.aiData)
      page[side] = {
        noteType: "mnemonic",
        noteData: aiNoteData,
      }
      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }
    if (contentType === "text") {
      // Changed from 'chat' to 'text'
      const updatedNote = { ...fullScreenNote }
      const page = { ...updatedNote.noteContents[pageIndex] }
      const addInfoData = {
        body: {
          noteId: fullScreenNote._id,
          pageIndex,
          updateData: userTopic || "Create text content", // Send user's topic
          noteType: "text", // Changed from 'chat' to 'text'
        },
        request: "add-Info-page",
      }
      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
      page[side] = {
        noteType: "text", // Changed from 'chat' to 'text'
        noteData: updateData.aiData,
      }
      updatedNote.noteContents[pageIndex] = page
      setFullScreenNote(updatedNote)
      setShowOptionsForPage(null)
    }
  }

  const handleAddNewPage = async () => {
    if (!fullScreenNote) return
    const refId = uuidv4()
    const updatedNote = { ...fullScreenNote }
    const addInfoData = {
      body: {
        noteId: fullScreenNote._id,
        updateData: {
          aSide: { noteType: "manual", noteData: refId },
          bSide: { noteType: "NONE", noteData: null },
        },
      },
      request: "add-Page",
    }
    const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
    updatedNote.noteContents.push({
      aSide: { noteType: "manual", noteData: refId },
      bSide: { noteType: "NONE", noteData: null },
    })
    setFullScreenNote(updatedNote)
    setTimeout(() => {
      const pagesContainer = document.getElementById("note-pages-container")
      if (pagesContainer) {
        pagesContainer.scrollTop = pagesContainer.scrollHeight
      }
    }, 100)
  }

  const handleAddAIContentToExcalidraw = async (contentType, userTopic) => {
    console.log("handleAddAIContentToExcalidraw called with:", contentType, "topic:", userTopic)
    if (!userTopic || !userTopic.trim()) {
      console.log("No topic provided, returning")
      return
    }
    try {
      console.log("Making API call for:", contentType, "with topic:", userTopic.trim())
      // Generate AI content using the existing API
      const addInfoData = {
        body: {
          noteId: excalidrawFullscreen._noteId,
          pageIndex: excalidrawFullscreen._pageIndex,
          updateData: userTopic.trim(),
          noteType: contentType === "text" ? "text" : contentType, // Changed from 'chat' to 'text'
        },
        request: "add-Info-page",
      }
      console.log("API payload:", addInfoData)
      const updateData = await postAws("https://api-dev.aurait.ai/notes/updateNotes", "POST", addInfoData)
      console.log("API response:", updateData)
      let aiContent
      if (contentType === "text") {
        // Changed from 'chat' to 'text'
        aiContent = updateData.aiData
      } else {
        aiContent = JSON.parse(updateData.aiData)
      }
      console.log("Parsed AI content:", aiContent)
      // Create a text element in Excalidraw with the AI content
      const refId =
        typeof excalidrawFullscreen.noteData === "string" && excalidrawFullscreen.noteData.includes("/")
          ? excalidrawFullscreen.noteData.split("/").pop().replace(".json", "")
          : excalidrawFullscreen.noteData
      const excalidrawAPI = excalidrawRefs.current[refId]
      console.log("Excalidraw API found:", !!excalidrawAPI, "for refId:", refId)
      if (excalidrawAPI) {
        // Format content based on type
        let displayText = ""
        if (contentType === "flashcard") {
          displayText = `FLASHCARD\n\nQ: ${aiContent.front || "No question"}\n\nA: ${aiContent.back || "No answer"}`
        } else if (contentType === "mnemonic") {
          displayText = `MNEMONIC DEVICE\n\nTechnique: ${aiContent.technique || "N/A"}\n\nExample: ${aiContent.example || "N/A"}`
        } else if (contentType === "mindmap") {
          displayText = `MIND MAP\n\n${aiContent.nodes ? aiContent.nodes.map((node, i) => `${i + 1}. ${node.title}: ${node.content}`).join("\n") : "Mind map content"}`
        } else if (contentType === "flowchart") {
          displayText = `FLOWCHART\n\n${aiContent.steps ? aiContent.steps.map((step, i) => `${i + 1}. ${step}`).join("\n") : "Flowchart content"}`
        } else if (contentType === "text") {
          // Changed from 'chat' to 'text'
          displayText = `TEXT NOTES\n\n${aiContent}`
        }
        console.log("Display text:", displayText)
        // Add text element to Excalidraw
        const textElement = {
          id: `ai-content-${Date.now()}`,
          type: "text",
          x: 100,
          y: 100,
          width: 400,
          height: 200,
          angle: 0,
          strokeColor: "#1e1e1e",
          backgroundColor: "#ffffff",
          fillStyle: "solid",
          strokeWidth: 1,
          strokeStyle: "solid",
          roughness: 1,
          opacity: 100,
          text: displayText,
          fontSize: 16,
          fontFamily: 1,
          textAlign: "left",
          verticalAlign: "top",
          containerId: null,
          originalText: displayText,
          lineHeight: 1.25,
        }
        const currentElements = excalidrawAPI.getSceneElements()
        console.log("Current elements count:", currentElements.length)
        excalidrawAPI.updateScene({
          elements: [...currentElements, textElement],
        })
        console.log("Text element added to Excalidraw")
        // Show success message
        const successMessage = document.createElement("div")
        successMessage.className = "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
        successMessage.textContent = `${contentType} added to drawing!`
        document.body.appendChild(successMessage)
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage)
          }
        }, 3000)
      } else {
        console.error("Excalidraw API not found")
        throw new Error("Excalidraw API not available")
      }
    } catch (error) {
      console.error("Error generating AI content:", error)
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = "Failed to generate AI content. Please try again."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 3000)
    }
  }

  const renderOptionsMenu = (pageIndex, side, infoSide) => {
    if (showOptionsForPage !== `${pageIndex}-${side}`) return null
    const handleOptionClick = (contentType) => {
      if (contentType === "manual") {
        handleAddNewContentType(pageIndex, side, contentType)
      } else {
        // Prompt user for topic for AI-generated content
        const userTopic = prompt(`Enter topic for ${contentType}:`)
        if (userTopic && userTopic.trim()) {
          handleAddNewContentType(pageIndex, side, contentType, userTopic.trim())
        }
      }
    }
    return (
      <div className="options-menu absolute bottom-14 right-4 bg-white rounded-lg shadow-lg p-2 z-50 border border-gray-200">
        <div className="flex flex-col gap-1">
          <button
            onClick={() => handleOptionClick("manual")}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <PenTool size={16} className="text-purple-500" />
            <span>Manual</span>
          </button>
          <button
            onClick={() => handleOptionClick("flashcard")}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <BookOpen size={16} className="text-orange-500" />
            <span>Flashcard</span>
          </button>
          <button
            onClick={() => handleOptionClick("mnemonic")}
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <Bot size={16} className="text-indigo-500" />
            <span>Mnemonics</span>
          </button>
          <button
            onClick={() => handleOptionClick("text")} // Changed from 'chat' to 'text'
            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm"
          >
            <MessageCircle size={16} className="text-blue-500" />
            <span>Text</span>
          </button>
        </div>
      </div>
    )
  }

  const renderNotePage = (page, index) => {
    const { aSide, bSide } = page
    if (bSide.noteType === "NONE") {
      return (
        <div className="h-[500px] w-full bg-gray-50 rounded-lg p-4 relative mb-6 flex-shrink-0 snap-center">
          <div className="w-full h-full relative">
            {renderNotePreview(aSide, fullScreenNote?._id, index, "aSide")}
            <div className="absolute bottom-4 right-4 z-50">
              <button
                className="p-2 bg-blue-500 rounded-full text-white hover:bg-blue-600 transition-colors shadow-md"
                title="Add content"
                onClick={() => setShowOptionsForPage(`${index}-bSide`)}
              >
                <Plus size={20} />
              </button>
              {renderOptionsMenu(index, "bSide", aSide)}
            </div>
          </div>
        </div>
      )
    }
    return (
      <div className="h-[500px] min-w-full flex flex-col md:flex-row gap-4 relative mb-6 flex-shrink-0 snap-center">
        <div className="flex-1 bg-gray-50 rounded-lg p-4 relative">
          {renderNotePreview(aSide, fullScreenNote?._id, index, "aSide")}
        </div>
        <div className="flex-1 bg-gray-50 rounded-lg p-4 relative">
          {renderNotePreview(bSide, fullScreenNote?._id, index, "bSide")}
          {(aSide.noteType === "NONE" || bSide.noteType === "NONE") && (
            <div className="absolute bottom-4 right-4 z-50">
              <button
                className="p-2 bg-blue-500 rounded-full text-white hover:bg-blue-600 transition-colors shadow-md"
                title="Add new page"
                onClick={() => setShowOptionsForPage(`${index}-bSide`)}
              >
                <Plus size={20} />
              </button>
              {renderOptionsMenu(index, "bSide", aSide)}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderNoteCard = (note) => {
    const firstPage = note?.noteContents?.[0] || {
      aSide: { noteType: "manual", noteData: null },
      bSide: { noteType: "NONE", noteData: null },
    }
    return (
      <div
        key={note._id}
        className={`bg-white rounded-xl p-3 sm:p-4 border border-gray-100 transition-all duration-300 ease-in-out hover:shadow-md ${
          favoriteNotes.includes(note._id) ? "ring-2 ring-blue-200" : ""
        }`}
      >
        <div className="flex justify-between items-start mb-2 sm:mb-3">
          <h3 className="text-base sm:text-lg font-bold text-gray-800 line-clamp-1">
            {note.noteTitle || "Untitled Note"}
          </h3>
          <div className="flex gap-1">
            <button
              className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
              title="Delete note"
              onClick={() => openDeleteModal({ type: "note", id: note._id, title: note.noteTitle })}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
          <div className="flex items-center bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium text-gray-600">
            {getNoteTypeIcon(firstPage.aSide?.noteType)}
            <span className="ml-1 sm:ml-1.5">{formatNoteType(firstPage.aSide?.noteType)}</span>
          </div>
          <div className="text-xs text-gray-500">{formatDate(note.createdAt || new Date())}</div>
          <div className="text-xs text-gray-500">{(note.noteContents && note.noteContents.length) || 0} pages</div>
        </div>
        <div className="bg-gradient-to-r from-gray-50 to-white p-2 sm:p-4 rounded-lg border border-gray-200 h-32 sm:h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:shadow-inner transition-all duration-300 ease-in-out">
          {renderNotePreview(firstPage.aSide, note._id, 0, "aSide")}
        </div>
        <div className="mt-2 sm:mt-3 flex justify-end">
          <button
            onClick={() => openFullScreenNote(note)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center transition-colors"
          >
            <span>View full note</span>
            <ArrowUpRight size={14} className="ml-1" />
          </button>
        </div>
      </div>
    )
  }

  const renderFolderContent = (noteFolder, folderNotes) => {
    const sortedNotes = sortNotes(folderNotes)
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {sortedNotes.length > 0 ? (
          sortedNotes.map((note) => renderNoteCard(note))
        ) : (
          <div className="col-span-full flex flex-col items-center justify-center py-6 sm:py-10 text-center">
            <div className="bg-gray-100 p-3 sm:p-4 rounded-full mb-3 sm:mb-4">
              <Search className="text-gray-400" size={20} />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">No matching notes found</h3>
            <p className="text-sm text-gray-500 max-w-md">
              Try adjusting your search terms or filters to find what you're looking for.
            </p>
          </div>
        )}
      </div>
    )
  }

  const formatNoteType = (type) => {
    if (!type) return "Unknown"
    const lowerType = type.toLowerCase()
    switch (lowerType) {
      case "flashcard":
        return "Flashcard"
      case "manual":
        return "Manual"
      case "text": // Changed from 'chat' to 'text'
        return "Text"
      case "flowchart":
        return "Flowchart"
      case "mindmap":
        return "Mind Map"
      case "mnemonic":
        return "Mnemonic"
      case "none":
        return "Empty"
      default:
        return type
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date)
  }

  const renderFolderRows = () => {
    const filteredNotes = getFilteredNotes()
    const folders = Object.entries(filteredNotes)
    if (folders.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 text-center">
          <div className="bg-blue-100 p-4 sm:p-6 rounded-full inline-flex mb-3 sm:mb-4">
            <Search className="text-blue-600" size={24} />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2 sm:mb-3">No notes found</h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto mb-4 sm:mb-6">
            {searchTerm
              ? "No notes match your search criteria. Try adjusting your search terms or filters."
              : "You don't have any notes yet. Create your first note to get started."}
          </p>
        </div>
      )
    }
    return (
      <div className="space-y-4 sm:space-y-6">
        {folders.map(([noteFolder, folderNotes]) => (
          <div key={noteFolder} className="animate-fade-in">
            {expandedFolder === noteFolder ? (
              // Combined container when expanded
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                {/* Folder header inside the same container */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 p-3 sm:p-4">
                  <button onClick={() => toggleFolder(noteFolder)} className="w-full flex items-center justify-between">
                    <div className="flex items-center">
                      <FolderOpen className="text-blue-600 mr-2 sm:mr-3" size={20} />
                      <div>
                        <span className="text-base sm:text-lg font-bold text-gray-800">{noteFolder}</span>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{folderNotes.length} notes</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setNewNoteData({ ...newNoteData, folderName: noteFolder })
                          setIsAddNoteModalOpen(true)
                        }}
                        className="p-1 sm:p-1.5 rounded-full text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                        title="Add note to this folder"
                      >
                        <Plus size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setSortOrder((prev) => (prev === "newest" ? "oldest" : "newest"))
                        }}
                        className="text-gray-500 hover:text-blue-600 p-1.5 sm:p-2 rounded-full hover:bg-blue-50 transition-colors"
                        title={sortOrder === "newest" ? "Sort by oldest" : "Sort by newest"}
                      >
                        <ArrowUpDown size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          openDeleteModal({ type: "folder", name: noteFolder })
                        }}
                        className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                        title="Delete folder"
                      >
                        <Trash2 size={16} />
                      </button>
                      <span className="p-1 sm:p-1.5 rounded-full bg-blue-100 text-blue-600">
                        <ChevronDown size={18} />
                      </span>
                    </div>
                  </button>
                </div>
                {/* Notes content in the same container */}
                <div className="p-3 sm:p-4">{renderFolderContent(noteFolder, folderNotes)}</div>
              </div>
            ) : (
              // Collapsed folder button
              <button
                onClick={() => toggleFolder(noteFolder)}
                className="w-full bg-white rounded-xl shadow-sm p-3 sm:p-4 flex items-center justify-between hover:shadow-md transition-all duration-300 ease-in-out border border-gray-100"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <Folder className="text-blue-500 mr-2 sm:mr-3" size={20} />
                    <div>
                      <span className="text-base sm:text-lg font-bold text-gray-800">{noteFolder}</span>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{folderNotes.length} notes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        openDeleteModal({ type: "folder", name: noteFolder })
                      }}
                      className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                      title="Delete folder"
                    >
                      <Trash2 size={16} />
                    </button>
                    <span className="p-1 sm:p-1.5 rounded-full text-gray-400">
                      <ChevronRight size={18} />
                    </span>
                  </div>
                </div>
              </button>
            )}
          </div>
        ))}
      </div>
    )
  }

  const renderExcalidrawFullscreen = () => {
    if (!excalidrawFullscreen) return null
    return (
      <div className="fixed inset-0 bg-white z-[60] flex flex-col">
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Drawing Editor</h3>
          <button
            onClick={closeExcalidrawFullscreen}
            className="p-2 rounded-full text-gray-600 hover:bg-gray-100"
            title="Close drawing editor"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="flex-1 p-4 overflow-hidden">
          <div className="w-full h-full bg-white rounded-lg relative">
            {loadingStates[excalidrawFullscreen.noteData] && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-30">
                <div className="flex flex-col items-center gap-4 text-blue-600 text-lg">
                  <Loader className="w-8 h-8 animate-spin" />
                  <span>Loading drawing...</span>
                  <div className="text-sm text-gray-500">Please wait while we fetch your drawing</div>
                </div>
              </div>
            )}
            <Excalidraw
              viewModeEnabled={false}
              zenModeEnabled={false}
              gridModeEnabled={true}
              theme="light"
              className="w-full h-full"
              ref={(api) => {
                if (api && excalidrawFullscreen.noteData) {
                  const refId =
                    typeof excalidrawFullscreen.noteData === "string" && excalidrawFullscreen.noteData.includes("/")
                      ? excalidrawFullscreen.noteData.split("/").pop().replace(".json", "")
                      : excalidrawFullscreen.noteData
                  excalidrawRefs.current[refId] = api
                  if (
                    typeof excalidrawFullscreen.noteData === "string" &&
                    excalidrawFullscreen.noteData.includes("/")
                  ) {
                    setTimeout(() => {
                      loadExcalidrawNote(excalidrawFullscreen.noteData, refId)
                    }, 100)
                  }
                }
              }}
            />
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <button
                onClick={() => {
                  const refId =
                    typeof excalidrawFullscreen.noteData === "string" && excalidrawFullscreen.noteData.includes("/")
                      ? excalidrawFullscreen.noteData.split("/").pop().replace(".json", "")
                      : excalidrawFullscreen.noteData
                  const noteTitle =
                    fullScreenNote?.noteTitle ||
                    notes.find((note) => note._id === excalidrawFullscreen._noteId)?.noteTitle ||
                    "drawing"
                  saveExcalidrawNote(
                    excalidrawFullscreen._noteId,
                    excalidrawFullscreen._pageIndex,
                    excalidrawFullscreen._side,
                    refId,
                    noteTitle,
                  )
                }}
                disabled={savingStates[excalidrawFullscreen.noteData] || loadingStates[excalidrawFullscreen.noteData]}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Save drawing"
              >
                {savingStates[excalidrawFullscreen.noteData] ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : loadingStates[excalidrawFullscreen.noteData] ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Drawing</span>
                  </>
                )}
              </button>
              {/* AI Content Options Menu */}
              {showOptionsForPage === `excalidraw-${excalidrawFullscreen._noteId}` && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg p-2 z-50 border border-gray-200 min-w-48">
                  <div className="flex flex-col gap-1">
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log("Flashcard button clicked - direct call")
                        // Don't close menu immediately, handle the action first
                        const userTopic = prompt(`Enter topic for flashcard:`)
                        console.log("User entered topic:", userTopic)
                        if (userTopic && userTopic.trim()) {
                          setShowOptionsForPage(null) // Close menu after getting input
                          handleAddAIContentToExcalidraw("flashcard", userTopic.trim())
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm text-gray-700"
                    >
                      <BookOpen size={16} className="text-orange-500" />
                      <span>Generate Flashcard</span>
                    </button>
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log("Mnemonic button clicked - direct call")
                        const userTopic = prompt(`Enter topic for mnemonic:`)
                        console.log("User entered topic:", userTopic)
                        if (userTopic && userTopic.trim()) {
                          setShowOptionsForPage(null)
                          handleAddAIContentToExcalidraw("mnemonic", userTopic.trim())
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm text-gray-700"
                    >
                      <Bot size={16} className="text-indigo-500" />
                      <span>Generate Mnemonic</span>
                    </button>
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log("Mind map button clicked - direct call")
                        const userTopic = prompt(`Enter topic for mind map:`)
                        console.log("User entered topic:", userTopic)
                        if (userTopic && userTopic.trim()) {
                          setShowOptionsForPage(null)
                          handleAddAIContentToExcalidraw("mindmap", userTopic.trim())
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm text-gray-700"
                    >
                      <Network size={16} className="text-pink-500" />
                      <span>Generate Mind Map</span>
                    </button>
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log("Flowchart button clicked - direct call")
                        const userTopic = prompt(`Enter topic for flowchart:`)
                        console.log("User entered topic:", userTopic)
                        if (userTopic && userTopic.trim()) {
                          setShowOptionsForPage(null)
                          handleAddAIContentToExcalidraw("flowchart", userTopic.trim())
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm text-gray-700"
                    >
                      <GitBranch size={16} className="text-green-500" />
                      <span>Generate Flowchart</span>
                    </button>
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log("Text notes button clicked - direct call")
                        const userTopic = prompt(`Enter topic for text notes:`)
                        console.log("User entered topic:", userTopic)
                        if (userTopic && userTopic.trim()) {
                          setShowOptionsForPage(null)
                          handleAddAIContentToExcalidraw("text", userTopic.trim()) // Changed from 'chat' to 'text'
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-md text-sm text-gray-700"
                    >
                      <MessageCircle size={16} className="text-blue-500" />
                      <span>Generate Text Notes</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderFullscreenContent = () => {
    if (!fullscreenContent) return null
    return (
      <div className="fixed inset-0 bg-white z-[60] flex flex-col">
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">{fullscreenContent.noteType}</h3>
          <button
            onClick={() => setFullscreenContent(null)}
            className="p-2 rounded-full text-gray-600 hover:bg-gray-100"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="flex-1 p-4 overflow-auto">
          <div className="w-full h-full">
            {fullscreenContent.noteType?.toLowerCase() === "flashcard" ? (
              <Flashcard front={fullscreenContent?.noteData?.front} back={fullscreenContent?.noteData?.back} />
            ) : fullscreenContent.noteType?.toLowerCase() === "text" ? ( // Changed from 'chat' to 'text'
              <div className="p-4 bg-white rounded-lg max-w-4xl mx-auto">{renderLatex(fullscreenContent.noteData)}</div>
            ) : fullscreenContent.noteType?.toLowerCase() === "flowchart" ? (
              <div className="w-full h-full min-h-[80vh]">
                <FlowChart message={{ flowchart: fullscreenContent.noteData }} />
              </div>
            ) : fullscreenContent.noteType?.toLowerCase() === "mnemonic" ? (
              <div className="w-full h-full min-h-[80vh]">
                <MnemonicDevice message={{ mnemonicDevice: fullscreenContent.noteData }} />
              </div>
            ) : fullscreenContent.noteType?.toLowerCase() === "mindmap" ? (
              <div className="w-full h-full min-h-[80vh]">
                <MindMap message={{ mindmap: fullscreenContent.noteData }} />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <Info size={20} className="mr-2" />
                <span>Unsupported note type: {fullscreenContent.noteType}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderFullScreenNote = () => {
    if (!fullScreenNote) return null
    const totalPages = fullScreenNote.noteContents?.length || 0
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
        <div className="max-w-full mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <button
              onClick={closeFullScreenNote}
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft size={18} className="mr-1 sm:mr-2" />
              <span className="text-sm sm:text-base">Back to notes</span>
            </button>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                className="p-1.5 sm:p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                title="Delete note"
                onClick={() =>
                  openDeleteModal({ type: "note", id: fullScreenNote._id, title: fullScreenNote.noteTitle })
                }
              >
                <Trash2 size={18} />
              </button>
              <button
                onClick={closeFullScreenNote}
                className="p-1.5 sm:p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors ml-1 sm:ml-2"
                title="Close and return to notes"
              >
                <XCircle size={18} />
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{fullScreenNote.noteTitle}</h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="flex items-center bg-white bg-opacity-70 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                  <FolderOpen size={14} className="text-blue-500 mr-1 sm:mr-1.5" />
                  <span>{fullScreenNote.noteFolder || "Uncategorized"}</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-70 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                  <Calendar size={14} className="text-gray-500 mr-1 sm:mr-1.5" />
                  <span>{formatDate(fullScreenNote.createdAt || new Date())}</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-70 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium text-gray-700 shadow-sm">
                  <span>{totalPages} pages</span>
                </div>
              </div>
            </div>
            <div
              id="note-pages-container"
              className="p-4 sm:p-6 md:p-8 bg-white overflow-y-auto max-h-[70vh] flex flex-col items-center w-full"
            >
              {(fullScreenNote.noteContents || []).map((page, index) => (
                <div key={index} className="w-full">
                  {renderNotePage(page, index)}
                </div>
              ))}
              <div className="mb-8 mt-2">
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  onClick={handleAddNewPage}
                >
                  <Plus size={18} className="mr-2" />
                  Add New Page
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDeleteModal = () => {
    if (!isDeleteModalOpen || !deleteTarget) return null
    const isFolder = deleteTarget.type === "folder"
    const title = isFolder ? deleteTarget.name : deleteTarget.title
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 animate-fade-in">
          <div className="flex items-center mb-4">
            <div className="bg-red-100 p-3 rounded-full mr-4">
              <Trash2 className="text-red-500" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">{isFolder ? "Delete Folder" : "Delete Note"}</h2>
          </div>
          <p className="text-gray-600 mb-6">
            {isFolder
              ? `Are you sure you want to delete the folder "${title}" and all its notes? This action cannot be undone.`
              : `Are you sure you want to delete the note "${title}"? This action cannot be undone.`}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={() => (isFolder ? handleDeleteFolder(deleteTarget.name) : handleDeleteNote(deleteTarget.id))}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader size={16} className="animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Delete {isFolder ? "Folder" : "Note"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const filterOptions = [
    { id: "all", label: "All Notes", icon: <FileText size={16} /> },
    { id: "flashcard", label: "Flashcards", icon: <BookOpen size={16} /> },
    { id: "manual", label: "Manual Notes", icon: <PenTool size={16} /> },
    { id: "text", label: "Text Notes", icon: <MessageCircle size={16} /> }, // Changed from 'chat' to 'text'
    { id: "flowchart", label: "Flowcharts", icon: <GitBranch size={16} /> },
    { id: "mindmap", label: "Mind Maps", icon: <Network size={16} /> },
  ]

  const noteTypeOptions = [
    {
      id: "manual",
      label: "Manual Drawing",
      icon: <PenTool size={20} className="text-purple-500" />,
      description: "Create with drawing tools",
    },
    {
      id: "flashcard",
      label: "Flashcard",
      icon: <BookOpen size={20} className="text-orange-500" />,
      description: "Question and answer cards",
    },
    {
      id: "mnemonic",
      label: "Mnemonic Device",
      icon: <Bot size={20} className="text-indigo-500" />,
      description: "Memory aid techniques",
    },
    {
      id: "mindmap",
      label: "Mind Map",
      icon: <Network size={20} className="text-pink-500" />,
      description: "Visual thinking tool",
    },
    {
      id: "flowchart",
      label: "Flowchart",
      icon: <GitBranch size={20} className="text-green-500" />,
      description: "Process diagrams",
    },
    {
      id: "text", // Changed from 'chat' to 'text'
      label: "Text Notes",
      icon: <MessageCircle size={20} className="text-blue-500" />,
      description: "Rich text content",
    },
  ]

  const handleCreateNote = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const refId = uuidv4()
      const newNote = {
        classRoomId: localStorage.getItem("classRoomId"),
        userId: localStorage.getItem("teacherId"),
        userType: "teacher",
        refId: `${newNoteData?.topic}_${refId}`,
        noteTitle: newNoteData?.topic,
        noteFolder: newNoteData?.folderName || "Uncategorized",
        noteContents: [
          {
            aSide: { noteType: "manual", noteData: refId },
            bSide: { noteType: "NONE", noteData: null },
          },
        ],
      }
      const aiData = await postAws("https://api-dev.aurait.ai/notes", "POST", newNote)
      const createdNote = {
        ...aiData.data,
        noteContents: aiData.data.noteContents || [
          {
            aSide: { noteType: "manual", noteData: refId },
            bSide: { noteType: "NONE", noteData: null },
          },
        ],
      }
      setNotes((prevNotes) => [...prevNotes, createdNote])
      const updatedOrganized = { ...organizedNotes }
      const noteFolder = newNote.noteFolder
      if (!updatedOrganized[noteFolder]) {
        updatedOrganized[noteFolder] = []
      }
      updatedOrganized[noteFolder].push(createdNote)
      setOrganizedNotes(updatedOrganized)
      setExpandedFolder(noteFolder)
      setIsAddNoteModalOpen(false)
      setNewNoteData({ folderName: "", topic: "" })
      setTimeout(() => {
        openFullScreenNote(createdNote)
      }, 100)
    } catch (error) {
      console.error("Error creating note:", error)
      setError("Failed to create note. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const getFilterLabel = () => {
    switch (activeFilter) {
      case "flashcard":
        return "Flashcards"
      case "manual":
        return "Manual"
      case "text": // Changed from 'chat' to 'text'
        return "Text"
      case "flowchart":
        return "Flowcharts"
      case "mindmap":
        return "Mind Maps"
      default:
        return "All"
    }
  }

  const totalNotes = notes.length
  const totalFolders = Object.keys(organizedNotes).length
  const flashcardNotes = notes.filter((note) => {
    if (!note?.noteContents || !Array.isArray(note.noteContents)) {
      return false
    }
    return note.noteContents.some((page) => {
      if (!page?.aSide || !page?.bSide) {
        return false
      }
      return (
        (page.aSide.noteType && page.aSide.noteType === "flashcard") ||
        (page.bSide.noteType && page.bSide.noteType === "flashcard")
      )
    })
  }).length
  const manualNotes = notes.filter((note) => {
    if (!note?.noteContents || !Array.isArray(note.noteContents)) {
      return false
    }
    return note.noteContents.some((page) => {
      if (!page?.aSide || !page?.bSide) {
        return false
      }
      return (
        (page.aSide.noteType && page.aSide.noteType === "manual") ||
        (page.bSide.noteType && page.bSide.noteType === "manual")
      )
    })
  }).length

  const digitisedStats = {
    total: digitisedNotes.length,
    creating: digitisedNotes.filter((note) => note.status?.toLowerCase() === "creating").length,
    processing: digitisedNotes.filter((note) => note.status?.toLowerCase() === "processing").length,
    completed: digitisedNotes.filter((note) => note.status?.toLowerCase() === "completed").length,
    failed: digitisedNotes.filter((note) => note.status?.toLowerCase() === "failed").length,
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading your notes</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your notes...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={() => {
              setNotesFetched(false)
              setError(null)
            }}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile view */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                {/* View Toggle */}
                <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                  <button
                    onClick={() => setViewMode("card")}
                    className={`p-1.5 rounded-l-lg transition-colors ${
                      viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <Grid3X3 size={12} />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 rounded-r-lg transition-colors ${
                      viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <List size={12} />
                  </button>
                </div>
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {filterOptions.map(({ id, label, icon }) => (
                        <button
                          key={id}
                          onClick={() => {
                            setActiveFilter(id)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left flex items-center transition-colors hover:bg-gray-50 ${
                            activeFilter === id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          <span className="mr-1.5">{icon}</span>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {activeTab === "digitised" && (
                  <button
                    onClick={() => setIsPdfUploadModalOpen(true)}
                    className="bg-white/20 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-1 border border-white/30 text-xs font-medium"
                  >
                    <FileUp size={12} />
                    <span>PDF</span>
                  </button>
                )}
                {activeTab === "notes" && (
                  <button
                    onClick={() => setIsAddNoteModalOpen(true)}
                    className="bg-white/20 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-1 border border-white/30 text-xs font-medium"
                  >
                    <Plus size={12} />
                    <span>Create</span>
                  </button>
                )}
              </div>
            </div>
            {/* Desktop view */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* View Toggle */}
              <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                <button
                  onClick={() => setViewMode("card")}
                  className={`flex items-center gap-1 p-2 rounded-l-lg transition-colors text-xs ${
                    viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="Card View"
                >
                  <Grid3X3 size={14} />
                  <span>Card</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1 p-2 rounded-r-lg transition-colors text-xs ${
                    viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="List View"
                >
                  <List size={14} />
                  <span>List</span>
                </button>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search notes"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <button
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                    >
                      <Filter size={14} />
                      <span>{getFilterLabel()}</span>
                      <ChevronDown
                        size={14}
                        className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                      />
                    </button>
                    {showFilterDropdown && (
                      <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                        {filterOptions.map(({ id, label, icon }) => (
                          <button
                            key={id}
                            onClick={() => {
                              setActiveFilter(id)
                              setShowFilterDropdown(false)
                            }}
                            className={`w-full px-3 py-2 text-sm text-left flex items-center transition-colors hover:bg-gray-50 ${
                              activeFilter === id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                            }`}
                          >
                            <span className="mr-2">{icon}</span>
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {activeTab === "digitised" && (
                    <button
                      onClick={() => setIsPdfUploadModalOpen(true)}
                      className="bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-2 border border-white/30 text-sm font-medium"
                    >
                      <FileUp size={16} />
                      <span>Digitise PDF Notes</span>
                    </button>
                  )}
                  {activeTab === "notes" && (
                    <button
                      onClick={() => setIsAddNoteModalOpen(true)}
                      className="bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-2 border border-white/30 text-sm font-medium"
                    >
                      <Plus size={16} />
                      <span>Create Note</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="p-3 md:p-6">
            {activeTab === "notes" && (
              <>
                {getFlattenedFilteredNotes().length === 0 && searchTerm === "" && activeFilter === "all" ? (
                  <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                      <Folder className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Your library is empty</h3>
                    <p className="text-gray-500 max-w-md mx-auto mb-6">
                      Create your first note to start organizing your knowledge.
                    </p>
                    <button
                      onClick={() => setIsAddNoteModalOpen(true)}
                      className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center gap-2 mx-auto"
                    >
                      <Plus size={20} />
                      <span>Create Your First Note</span>
                    </button>
                  </div>
                ) : getFlattenedFilteredNotes().length === 0 ? (
                  <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-indigo-100 text-indigo-600`}
                    >
                      <Search className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No matching notes found</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Try adjusting your search terms or filters to find what you're looking for.
                    </p>
                  </div>
                ) : viewMode === "card" ? (
                  <div className="space-y-4 sm:space-y-6">
                    {Object.entries(getFilteredNotes()).map(([noteFolder, folderNotes]) => (
                      <div key={noteFolder} className="animate-fade-in">
                        {expandedFolder === noteFolder ? (
                          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 p-3 sm:p-4">
                              <button
                                onClick={() => toggleFolder(noteFolder)}
                                className="w-full flex items-center justify-between"
                              >
                                <div className="flex items-center">
                                  <FolderOpen className="text-blue-600 mr-2 sm:mr-3" size={20} />
                                  <div>
                                    <span className="text-base sm:text-lg font-bold text-gray-800">{noteFolder}</span>
                                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                                      {folderNotes.length} notes
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setNewNoteData({ ...newNoteData, folderName: noteFolder })
                                      setIsAddNoteModalOpen(true)
                                    }}
                                    className="p-1 sm:p-1.5 rounded-full text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                                    title="Add note to this folder"
                                  >
                                    <Plus size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSortOrder((prev) => (prev === "newest" ? "oldest" : "newest"))
                                    }}
                                    className="text-gray-500 hover:text-blue-600 p-1.5 sm:p-2 rounded-full hover:bg-blue-50 transition-colors"
                                    title={sortOrder === "newest" ? "Sort by oldest" : "Sort by newest"}
                                  >
                                    <ArrowUpDown size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openDeleteModal({ type: "folder", name: noteFolder })
                                    }}
                                    className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                                    title="Delete folder"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                  <span className="p-1 sm:p-1.5 rounded-full bg-blue-100 text-blue-600">
                                    <ChevronDown size={18} />
                                  </span>
                                </div>
                              </button>
                            </div>
                            <div className="p-3 sm:p-4">{renderFolderContent(noteFolder, folderNotes)}</div>
                          </div>
                        ) : (
                          <button
                            onClick={() => toggleFolder(noteFolder)}
                            className="w-full bg-white rounded-xl shadow-sm p-3 sm:p-4 flex items-center justify-between hover:shadow-md transition-all duration-300 ease-in-out border border-gray-100"
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center">
                                <Folder className="text-blue-500 mr-2 sm:mr-3" size={20} />
                                <div>
                                  <span className="text-base sm:text-lg font-bold text-gray-800">{noteFolder}</span>
                                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{folderNotes.length} notes</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    openDeleteModal({ type: "folder", name: noteFolder })
                                  }}
                                  className="p-1 sm:p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
                                  title="Delete folder"
                                >
                                  <Trash2 size={16} />
                                </button>
                                <span className="p-1 sm:p-1.5 rounded-full text-gray-400">
                                  <ChevronRight size={18} />
                                </span>
                              </div>
                            </div>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // List View
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Note Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Folder
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Created Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getFlattenedFilteredNotes().map((note) => {
                            const firstPageType = note?.noteContents?.[0]?.aSide?.noteType || "NONE"
                            return (
                              <tr key={note._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                                      <FileText className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                      {note.noteTitle || "Untitled Note"}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {note.noteFolder || "Uncategorized"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div className="flex items-center">
                                    <Calendar size={14} className="mr-2 text-gray-400" />
                                    {formatDate(note.createdAt || new Date())}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {formatNoteType(firstPageType)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => openFullScreenNote(note)}
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                                    >
                                      <Edit size={14} className="mr-1" />
                                      Edit
                                    </button>
                                    <div className="relative">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setActiveNoteMenu(activeNoteMenu === note._id ? null : note._id)
                                        }}
                                        className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-400 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                                        aria-label="Note options"
                                      >
                                        <MoreVertical size={16} />
                                      </button>
                                      {activeNoteMenu === note._id && (
                                        <div className="options-menu-list absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-10 border border-gray-100 py-1 animate-scale-in">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              openDeleteModal({ type: "note", id: note._id, title: note.noteTitle })
                                              setActiveNoteMenu(null)
                                            }}
                                            className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                                          >
                                            <Trash2 size={16} className="mr-2" />
                                            Delete Note
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {isAddNoteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Add New Note</h2>
              <button
                onClick={() => setIsAddNoteModalOpen(false)}
                className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateNote} className="space-y-4">
              <div>
                <label htmlFor="topic" className="block text-sm font-medium text-gray-700">
                  Topic
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="topic"
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter topic"
                    value={newNoteData.topic}
                    onChange={(e) => setNewNoteData({ ...newNoteData, topic: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="folderName" className="block text-sm font-medium text-gray-700">
                  Folder Name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="folderName"
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter folder name"
                    value={newNoteData.folderName}
                    onChange={(e) => setNewNoteData({ ...newNoteData, folderName: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsAddNoteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader size={16} className="animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={16} className="mr-2" />
                      Create Note
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isPdfUploadModalOpen && <UploadPdfForDigitisation onClose={() => setIsPdfUploadModalOpen(false)} />}
      {renderFullScreenNote()}
      {renderExcalidrawFullscreen()}
      {renderFullscreenContent()}
      {renderDeleteModal()}
    </div>
  )
}

export default Notes
